import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ArticleDTO, ReceiptDTO } from "../../../../models/ArticleDTO";
import { kitDeliveryStatus, owedArticleStatus } from "../../../../constants";
import DeliveryPrompt, { DeliveryAction } from "../../../../components/kits/deliveryPrompt";
import DeliveryBatchPrompt from "../../../../components/kits/deliveryBatchPrompt";
import { DeliveryBatchEntry } from "../../../../services/v2/kits.service";
import KitHistoryTimeline from "../../../../components/kits/kitHistoryTimeline";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import Card from "../../../../components/card";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import useEsTelefono from "../../../../utils/esTelefono";
import { KitFilters, PER_PAGE_DEFAULT, Pager, apiError, bestEnrollment, daysAgo, saveExcel, today, useCampus, useFilterOptions, useIdleLocked, usePerms } from "./shared";
import FiltrosTienda, { FILTRO_VACIO, FiltroTienda, aKitFilters } from "../ventas/filtrosTienda";
import { Chip, Compro, Dato, EntregaChip, Producto, Sub, Ventana, ampm, monto, vendidoPor } from "../ventas/piezasTienda";
import "../../managment/enrollmentRequests/filtrosMatriculas.css";
import "../ventas/ventas.css";

/*
	Tienda → Entregas (sección "kits"; antes «Control de Entregas»). Módulos Claros, Fase 1 (2026-11-01):
	  Por Entregar (con número) · Entregados · Faltan por Comprar (antes «Vendidos sin Inventario»; en la Fase 2 se va a Compras).
	  Filtros a la vista: Buscar, Producto, Entrega, Fecha de Pago; Curso, Periodo, Horario, Maestra y Día en «Más Filtros».
	  La Fecha de Pago es el ÚLTIMO pago, lo mismo que la columna y el orden (antes el filtro decía «Primer Pago»).
	  Clic en la fila = ventana con los datos y el historial; «Registrar Entrega» / «Entregar Factura» siguen en la fila.
	La entrega (firma, comprobante, piezas, «Sale de», entrega por factura) es la misma de siempre: DeliveryPrompt y DeliveryBatchPrompt
	sin cambios de lógica. ?q= sigue funcionando (Promociones › «Ir a Entregar» llega con el correlativo).
*/

type TabKey = "por_entregar" | "entregados" | "faltan";
const TABS: { key: TabKey; label: string; tab: string }[] = [
	{ key: "por_entregar", label: "Por Entregar", tab: "paid_undelivered" },
	{ key: "entregados", label: "Entregados", tab: "delivered" },
	{ key: "faltan", label: "Faltan por Comprar", tab: "undelivered" }
];
const SECTION = "kits";
/*
	Por Entregar y Faltan por Comprar no llevan fecha: un kit pagado hace meses y sin entregar también se debe. Para no llenarlas
	con los miles de ventas viejas «Sin Registro» abren con Entrega = Sin Entregar + Entrega Parcial. Entregados abre con 90 días.
*/
const OWED_STATUSES = [String(kitDeliveryStatus.PENDING), String(kitDeliveryStatus.PARTIAL)];
const baseDe = (key: TabKey): FiltroTienda => key === "entregados"
	? { ...FILTRO_VACIO, preset: "rango", from: daysAgo(90), to: today() }
	: { ...FILTRO_VACIO, entrega: OWED_STATUSES };

/** Rows of the same student and invoice together, in the order the first one appears (no invoice = alone). */
const invoiceGroups = (rows: ArticleDTO[]): ArticleDTO[][] => {
	const out: ArticleDTO[][] = [];
	const byKey: Record<string, ArticleDTO[]> = {};
	rows.forEach((row) => {
		const key = row.invoiceId ? row.studentId + "|" + row.invoiceId : null;
		if (key && byKey[key]) { byKey[key].push(row); return; }
		const group = [row];
		if (key) byKey[key] = group;
		out.push(group);
	});
	return out;
};

const ControlEntregas: React.FC = () => {
	const { campusId, currency } = useCampus();
	const movil = useEsTelefono();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms(SECTION);
	const canDeliver = !!perms.create;
	const canFix = !!perms.update;
	const options = useFilterOptions(campusId, SECTION);
	const http = HttpManager.getInstance();

	const [tabKey, setTabKey] = useState<TabKey>("por_entregar");
	const tab = TABS.find((t) => t.key === tabKey) || TABS[0];
	// «Ir a Entregar» desde Promociones llega con ?q=<alumno> (2026-11-01).
	const inicialQ = new URLSearchParams(window.location.search).get("q") || "";
	const [search, setSearch] = useState<string>(inicialQ);
	const [q, setQ] = useState<string>(inicialQ);
	const [filtro, setFiltro] = useState<FiltroTienda>(baseDe("por_entregar"));
	const [page, setPage] = useState<number>(1);
	const [rows, setRows] = useState<ArticleDTO[]>([]);
	const [groups, setGroups] = useState<any[]>([]);
	const [openGroup, setOpenGroup] = useState<number | null>(null);
	const [groupRows, setGroupRows] = useState<Record<number, ArticleDTO[]>>({});
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [porEntregar, setPorEntregar] = useState<number | null>(null);
	const [exporting, setExporting] = useState<boolean>(false);
	const [deliveryKit, setDeliveryKit] = useState<ArticleDTO | null>(null);
	const [deliveryBusy, setDeliveryBusy] = useState<boolean>(false);
	const [deliveryError, setDeliveryError] = useState<string | null>(null);
	/** Entregar Factura (2026-10-29): the clicked product plus the other paid, still-owed products of its invoice. */
	const [batch, setBatch] = useState<{ kits: ArticleDTO[]; invoiceIds: number[] } | null>(null);
	/** Kit whose invoice is being looked up (its button shows "Abriendo…"). */
	const [opening, setOpening] = useState<number | null>(null);
	/** Result of the last delivery: the receipt went out by WhatsApp (ok) or not (warn, resend from Historial). Click dismisses. */
	const [deliveryNotice, setDeliveryNotice] = useState<{ text: string; ok: boolean } | null>(null);
	/** Window of one row: data, history and its delivery button. */
	const [detalle, setDetalle] = useState<ArticleDTO | null>(null);
	/** Only the latest request may paint the table (a slow answer of the previous tab/filter must not overwrite it). */
	const requestRef = useRef<number>(0);

	/**
	 * Esteban 2026-10-29: what was sold before the delivery control is "Sin Registro" and many of those are still owed. Searching
	 * a person in Por Entregar with the default Entrega also brings them; without a search the list stays as it was (thousands).
	 */
	const widened = tabKey === "por_entregar" && q.trim() !== ""
		&& filtro.entrega.length === OWED_STATUSES.length && OWED_STATUSES.every((id) => filtro.entrega.includes(id));
	const effective = useCallback((): KitFilters => ({
		...aKitFilters(tabKey === "entregados" ? { ...filtro, entrega: [] } : filtro, "lastPaid"), q: q.trim(), sort: "lastPaid", dir: "desc",
		...(widened ? { deliveryStatusIds: [...OWED_STATUSES, String(kitDeliveryStatus.UNKNOWN)] } : {})
	}), [filtro, q, widened, tabKey]);

	const load = useCallback(async () => {
		if (!campusId) return;
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		try {
			if (tabKey === "faltan") {
				const response = await http.kitsService.withoutInventory(campusId, SECTION, effective());
				if (request !== requestRef.current) return;
				setGroups(response.data || []);
				setTotal((response.data || []).length);
				setLastPage(1);
				setOpenGroup(null);
				setGroupRows({});
			} else {
				const response = await http.kitsService.list(campusId, SECTION, tab.tab, effective(), page, PER_PAGE_DEFAULT);
				if (request !== requestRef.current) return;
				setRows(response.data.data || []);
				setTotal(response.data.total || 0);
				setLastPage(response.data.last_page || 1);
			}
		} catch (requestError: any) {
			if (request !== requestRef.current) return;
			setError(apiError(requestError, "No se pudo cargar la información."));
			setRows([]);
			setGroups([]);
		} finally {
			if (request === requestRef.current) setLoading(false);
		}
	}, [campusId, tabKey, tab.tab, effective, page]);

	useEffect(() => { load(); }, [load]);

	/** Number on Por Entregar (the only tab with pending work), with its default filters. */
	const refreshCounts = useCallback(() => {
		if (!campusId) return;
		http.kitsService.list(campusId, SECTION, "paid_undelivered", { deliveryStatusIds: OWED_STATUSES }, 1, 1)
			.then((response) => setPorEntregar(response.data.total || 0))
			.catch(() => undefined);
	}, [campusId]);
	useEffect(() => { refreshCounts(); }, [refreshCounts]);

	const switchTab = (key: TabKey) => {
		// Fechas y Entrega son de cada pestaña (Entregados no lleva Entrega): toman lo de la pestaña nueva; lo demás se queda.
		const next = baseDe(key);
		setFiltro((previous) => ({ ...previous, preset: next.preset, from: next.from, to: next.to, entrega: next.entrega }));
		setTabKey(key); setPage(1); setRows([]);
	};

	const afterChange = async () => { await load(); refreshCounts(); if (openGroup !== null) loadGroup(openGroup, true); };

	/** Registering the delivery never fails because of the WhatsApp: the receipt says whether it went out. */
	const receiptNotice = (receipt: ReceiptDTO | null | undefined): { text: string; ok: boolean } | null => {
		if (!receipt) return null;
		if (receipt.waStatus === "enviado") return { ok: true, text: "Entrega registrada. Comprobante enviado por WhatsApp al " + (receipt.phone || "alumno") + "." };
		return { ok: false, text: "Entrega registrada. El comprobante no se envió: " + (receipt.waMessage || "sin detalle") + ". Puedes reenviarlo desde el Historial." };
	};
	useEffect(() => {
		if (!deliveryNotice) return;
		const timer = window.setTimeout(() => setDeliveryNotice(null), deliveryNotice.ok ? 10000 : 20000);
		return () => window.clearTimeout(timer);
	}, [deliveryNotice]);

	const onDelivery = async (action: DeliveryAction, note: string, warehouseId?: number | null) => {
		if (!deliveryKit) return;
		setDeliveryBusy(true);
		setDeliveryError(null);
		try {
			let notice: { text: string; ok: boolean } | null = null;
			if (action.kind === "items") {
				const response = await http.inventoryService.registerDeliveryItems(campusId, deliveryKit.id, action.items, note, warehouseId, action.signature, action.photo);
				notice = receiptNotice(response.data?.receipt);
			} else if (action.kind === "delivery") {
				const response = await http.kitsService.registerDelivery(campusId, deliveryKit.id, action.type, note, action.signature, action.photo,
					action.items !== undefined ? { items: action.items, previous: action.previous || "", warehouseId } : undefined);
				notice = receiptNotice(response.data?.receipt);
			} else if (action.kind === "note") await http.kitsService.addNote(campusId, deliveryKit.id, note);
			else await http.kitsService.setDeliveryStatus(campusId, deliveryKit.id, action.deliveryStatusId, note);
			setDeliveryKit(null);
			setDeliveryNotice(notice);
			await afterChange();
		} catch (requestError: any) {
			setDeliveryError(apiError(requestError, "No se pudo registrar la entrega."));
		} finally {
			setDeliveryBusy(false);
		}
	};

	/**
	 * What was bought together is delivered together: when the invoice has more products to hand over, "Entregar Factura"
	 * opens with all of them; otherwise (or if the lookup fails) the one-product Registrar Entrega, as before.
	 */
	const openDelivery = async (kit: ArticleDTO) => {
		setDetalle(null);
		setDeliveryError(null);
		const paidInFull = kit.statusId === owedArticleStatus.FULLY_PAID || kit.statusId === 3;
		if (!canDeliver || !paidInFull) { setDeliveryKit(kit); return; }
		setOpening(kit.id);
		try {
			const response = await http.kitsService.invoiceGroup(campusId, kit.id);
			const kits = response.data?.kits || [];
			if (kits.length > 1) setBatch({ kits, invoiceIds: response.data.invoiceIds || [] });
			else setDeliveryKit(kit);
		} catch (requestError) {
			setDeliveryKit(kit);
		} finally {
			setOpening(null);
		}
	};

	const onDeliveryBatch = async (entries: DeliveryBatchEntry[], signature: string, photo: File | null) => {
		setDeliveryBusy(true);
		setDeliveryError(null);
		try {
			const response = await http.kitsService.registerDeliveryBatch(campusId, entries, signature, photo);
			const receipt = response.data?.receipt;
			const notice = receipt ? receiptNotice(receipt) : { ok: false, text: response.data?.message + ". " + (response.data?.receiptError || "El comprobante no se generó.") };
			setBatch(null);
			setDeliveryNotice(notice && receipt ? { ...notice, text: notice.text.replace("Entrega registrada", response.data.message) } : notice);
			await afterChange();
		} catch (requestError: any) {
			setDeliveryError(apiError(requestError, "No se pudo registrar la entrega."));
		} finally {
			setDeliveryBusy(false);
		}
	};

	const loadGroup = async (articleId: number, force = false) => {
		if (!force && groupRows[articleId]) return;
		try {
			const response = await http.kitsService.list(campusId, SECTION, "undelivered", { ...effective(), articleIds: [String(articleId)] }, 1, 500);
			setGroupRows((previous) => ({ ...previous, [articleId]: response.data.data || [] }));
		} catch (requestError) {
			setGroupRows((previous) => ({ ...previous, [articleId]: [] }));
		}
	};
	const toggleGroup = (articleId: number) => { if (openGroup === articleId) { setOpenGroup(null); return; } setOpenGroup(articleId); loadGroup(articleId); };

	const exportExcel = async () => {
		setExporting(true);
		try {
			await saveExcel(campusId, SECTION, tabKey === "faltan" ? "inventory" : "kits", tab.tab, effective(), `entregas_${tabKey}_${today()}`);
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo generar el Excel."));
		} finally {
			setExporting(false);
		}
	};

	const deliverable = (kit: ArticleDTO): boolean => !kit.isCancelled && (kit.statusId === owedArticleStatus.FULLY_PAID || kit.statusId === owedArticleStatus.PARTLY_PAID) && kit.deliveryStatusId !== kitDeliveryStatus.DELIVERED;
	const fixable = (kit: ArticleDTO): boolean => !kit.isCancelled && kit.deliveryStatusId === kitDeliveryStatus.UNKNOWN;
	/** One button for the whole invoice: it opens Entregar Factura with every product still to hand over. */
	const groupButton = (group: ArticleDTO[]) => {
		const first = group.find((kit) => canDeliver && deliverable(kit)) || group.find((kit) => canFix && fixable(kit));
		if (!first) return null;
		const pending = group.filter(deliverable).length;
		return <button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ whiteSpace: "nowrap" }} disabled={idleLocked || opening !== null}
			onClick={(event) => { event.stopPropagation(); openDelivery(first); }}>
			{opening === first.id ? "Abriendo…" : pending > 1 ? "Entregar Factura" : "Registrar Entrega"}</button>;
	};

	const pagado = (kit: ArticleDTO) => <>{monto(currency, kit.paidAmount?.value)}{kit.isDebt ? <Sub><span className="wvtOro">Falta {monto(currency, kit.pendingAmount?.value)}</span></Sub>
		: tabKey !== "por_entregar" && kit.paidAmount?.value !== kit.subTotal?.value ? <Sub>de {monto(currency, kit.subTotal?.value)}</Sub> : null}</>;
	const curso = (kit: ArticleDTO) => {
		const e = bestEnrollment(kit);
		return e ? <>{e.courseBrief || e.module || e.level}<Sub>{[ampm(e.schedule || e.scheduleName || e.scheduleCode), e.mentor].filter(Boolean).join(", ")}</Sub></> : <span className="wvtSub">Sin Horario Activo</span>;
	};
	const tenue = { opacity: loading ? 0.6 : 1 };

	const kitTable = (data: ArticleDTO[]) => {
		const grupos = invoiceGroups(data);
		if (movil) return <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
			{grupos.map((group) => <div key={group[0].id} className="wvtTarjeta" style={tenue} role="button" tabIndex={0} onClick={() => setDetalle(group[0])}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
					<Compro kit={group[0]} enrollment={bestEnrollment(group[0])} />
					<div style={{ flexShrink: 0 }}><EntregaChip kit={group[0]} /></div>
				</div>
				{group.map((kit) => <div key={kit.id} style={{ fontSize: 14, marginTop: 4 }} onClick={(event) => { event.stopPropagation(); setDetalle(kit); }}>
					<Producto kit={kit} /> · {monto(currency, kit.paidAmount?.value)}{group.length > 1 ? <> <EntregaChip kit={kit} /></> : null}</div>)}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 6 }}>
					<Sub>{group[0].paidAt || "-"}{group.length > 1 ? ", Factura #" + group[0].invoiceId : ""}</Sub>
					{groupButton(group)}
				</div>
			</div>)}
		</div>;
		return <table className="table table-hover wvtLista">
			<thead><tr>
				<th>Alumna</th><th>Producto</th><th>Curso</th><th style={{ width: 100 }}>Último Pago</th>
				<th className="wvtNum" style={{ width: 120 }}>Pagado</th><th style={{ width: 130 }}>Entrega</th><th style={{ width: 130 }}>Vendido Por</th><th style={{ width: 150 }}></th>
			</tr></thead>
			<tbody>{grupos.map((group) => {
				const kit = group[0];
				const span = group.length;
				return group.map((item, index) => <tr key={item.id} className={"wvtFila" + (index < span - 1 ? " wvtGrupo" : "")} style={tenue} onClick={() => setDetalle(item)}>
					{index === 0 ? <td rowSpan={span}><Compro kit={kit} enrollment={null} />{span > 1 ? <Sub>Factura #{kit.invoiceId}, {span} productos</Sub> : null}</td> : null}
					<td><Producto kit={item} /></td>
					{index === 0 ? <td rowSpan={span}>{curso(kit)}</td> : null}
					{index === 0 ? <td rowSpan={span} style={{ whiteSpace: "nowrap" }}>{kit.paidAt || "-"}</td> : null}
					<td className="wvtNum">{pagado(item)}</td>
					<td><EntregaChip kit={item} />{item.deliveredAt ? <Sub>{item.deliveredAt}</Sub> : null}</td>
					{index === 0 ? <td rowSpan={span}>{vendidoPor(kit)}</td> : null}
					{index === 0 ? <td rowSpan={span} style={{ textAlign: "right" }}>{groupButton(group)}</td> : null}
				</tr>);
			})}</tbody>
		</table>;
	};

	const inventoryTable = () => <table className="table wvtLista">
		<thead><tr>
			<th style={{ width: 24 }}></th><th>Producto</th>
			<th className="wvtNum">En Inventario</th><th className="wvtNum">Por Entregar</th><th className="wvtNum">Alumnas</th>
			<th className="wvtNum">Pagados / Con Abono</th><th className="wvtNum">Faltan por Comprar</th><th>Pago Más Antiguo</th>
		</tr></thead>
		<tbody>{groups.map((group) => <React.Fragment key={group.articleId}>
			<tr className="wvtFila" onClick={() => toggleGroup(group.articleId)} style={{ background: openGroup === group.articleId ? "var(--wa-acento-suave)" : undefined }}>
				<td className="wvtSub">{openGroup === group.articleId ? "▾" : "▸"}</td>
				<td><b>{group.name}</b><Sub>{group.sku}</Sub></td>
				<td className="wvtNum">{group.stock}</td>
				<td className="wvtNum"><b>{group.units}</b>{group.units !== group.kits ? <Sub>{group.kits} kits</Sub> : null}</td>
				<td className="wvtNum">{group.students}</td>
				<td className="wvtNum">{group.paidFull} / {group.partial}</td>
				<td className="wvtNum">{group.shortage > 0 ? <Chip tono="oro">{group.shortage}</Chip> : <Chip>Completo</Chip>}</td>
				<td style={{ whiteSpace: "nowrap" }}>{group.oldestPaid || "-"}</td>
			</tr>
			{openGroup === group.articleId ? <tr><td colSpan={8} style={{ padding: "0 0 8px 24px", background: "var(--wa-papel-2)" }}>
				{groupRows[group.articleId] ? (groupRows[group.articleId].length ? kitTable(groupRows[group.articleId]) : <div className="wvtSub" style={{ padding: 12 }}>-</div>) : <div className="loader" />}
			</td></tr> : null}
		</React.Fragment>)}</tbody>
	</table>;

	if (!campusId) return <div style={{ padding: 24 }}>Selecciona un plantel.</div>;

	const vacio = <div className="wvtSub" style={{ textAlign: "center", padding: 24, fontSize: 15 }}>{tabKey === "faltan" ? "Nada pendiente de entregar con estos filtros." : "Sin registros con estos filtros."}</div>;

	return <div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
		<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
			<h4 style={{ margin: 0 }}>Entregas</h4>
			<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={idleLocked || exporting || total < 1} onClick={exportExcel}>{exporting ? "Generando…" : "Excel"}</button>
		</div>
		<Card>
			<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
					<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
						{TABS.map((t) => <li className="nav-item" key={t.key}>
							<button type="button" disabled={idleLocked} className={"nav-link btn btn-sm " + (t.key === tabKey ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => switchTab(t.key)}>
								{t.label}{t.key === "por_entregar" && porEntregar ? <span className="waTabCuenta">{porEntregar}</span> : null}
							</button>
						</li>)}
					</ul>
					<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
						<SearchInputComponent search={search} onChangeSearch={setSearch} onClickSearch={() => { setQ(search.trim()); setPage(1); }} onClearSearch={() => { setSearch(""); setQ(""); setPage(1); }} />
					</div>
				</div>
				<FiltrosTienda valor={filtro} onChange={(v) => { setFiltro(v); setPage(1); }} opciones={options} base={baseDe(tabKey)}
					etiquetaFecha="Pagados" entrega={tabKey !== "entregados"} producto mas />
				{error ? <div className="alert alert-danger" style={{ marginTop: 12 }}>{error}</div> : null}
				{tabKey !== "faltan" && lastPage > 1 ? <Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} /> : <div style={{ height: 6 }} />}
				<div className="waScrollX" style={{ overflowX: "auto", marginTop: 8 }}>
					{loading && !rows.length && !groups.length ? <div className="loader" />
						: tabKey === "faltan" ? (groups.length ? inventoryTable() : vacio)
						: rows.length ? kitTable(rows) : vacio}
				</div>
				{tabKey !== "faltan" ? <Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} /> : null}
			</div>
		</Card>

		{detalle ? <Ventana onClose={() => setDetalle(null)} titulo={<><span>{(detalle.name || detalle.title)}{" · " + (detalle.isThirdParty ? (detalle.buyerName || "Sin Dato") : (detalle.studentName || ""))}</span><EntregaChip kit={detalle} /></>}
			pie={<>
				{!detalle.isThirdParty && detalle.studentId ? <AccionesAlumno studentId={detalle.studentId} campusId={campusId} alinear="inicio" /> : null}
				<span style={{ flex: 1 }} />
				{(canDeliver && deliverable(detalle)) || (canFix && fixable(detalle))
					? <button type="button" className="btn btn-primary btn-sm" disabled={idleLocked || opening !== null} onClick={() => openDelivery(detalle)}>{opening === detalle.id ? "Abriendo…" : "Registrar Entrega"}</button> : null}
			</>}>
			<div className="wvtDatos">
				<Dato t="Pagado">{monto(currency, detalle.paidAmount?.value)}<Sub>de {monto(currency, detalle.subTotal?.value)}</Sub></Dato>
				{detalle.isDebt ? <Dato t="Falta"><span className="wvtOro">{monto(currency, detalle.pendingAmount?.value)}</span></Dato> : null}
				<Dato t="Último Pago">{detalle.paidAt || "-"}{detalle.paymentsCount && detalle.paymentsCount > 1 ? <Sub>Primer Pago {detalle.firstPaidAt}</Sub> : null}</Dato>
				<Dato t="Factura">{detalle.invoiceId ? "#" + detalle.invoiceId : "Sin Factura"}</Dato>
				<Dato t="Vendido Por">{vendidoPor(detalle)}</Dato>
				<Dato t={detalle.isThirdParty ? "Comprador" : "Curso"}>{detalle.isThirdParty ? <>{detalle.buyerName || "Sin Dato"}<Sub>{detalle.buyerPhone || ""}</Sub></> : curso(detalle)}</Dato>
				<Dato t="Código">{detalle.sku || "-"}{detalle.quantity > 1 ? <Sub>{detalle.quantity} piezas</Sub> : null}</Dato>
			</div>
			<div className="wvtSeccion">
				<h6>Historial</h6>
				<KitHistoryTimeline campusId={campusId} kitId={detalle.id} section={SECTION} compact />
			</div>
		</Ventana> : null}

		{batch ? <DeliveryBatchPrompt kits={batch.kits} invoiceIds={batch.invoiceIds} campusId={campusId} busy={deliveryBusy} error={deliveryError}
			onConfirm={onDeliveryBatch} onSingle={(kit) => { setBatch(null); setDeliveryError(null); setDeliveryKit(kit); }}
			onCancel={() => { if (!deliveryBusy) setBatch(null); }} /> : null}
		{deliveryKit ? <DeliveryPrompt kit={deliveryKit} campusId={campusId} busy={deliveryBusy} error={deliveryError} canCreate={canDeliver} canUpdate={canFix} onConfirm={onDelivery} onCancel={() => { if (!deliveryBusy) setDeliveryKit(null); }} /> : null}
		{deliveryNotice ? <div className={"alert " + (deliveryNotice.ok ? "alert-success" : "alert-warning") + " wvtAviso"} role="status" onClick={() => setDeliveryNotice(null)}>{deliveryNotice.text}</div> : null}
	</div>;
};

export default ControlEntregas;
